import { computed, ref, shallowRef } from 'vue'
import * as THREE from 'three'
import type { SelectableHint } from '@/composables/useMeshHighlighting'
import type { GizmoMode } from '@/composables/useTransformGizmo'
import {
  allocatesAllInstances,
  instanceCountOf,
  isInstanceAllocated,
  needsInstanceSelection,
  type EbomPartInstanceAllocation,
} from '@/utils/instanceAllocation'

export type SelectionTransforms = {
  instanceForMesh: (mesh: THREE.Mesh) => string | null
  isEditableInstance: (key: string) => boolean
  meshesForInstance: (key: string) => THREE.Mesh[]
  proxyStateFor: (key: string) => { position: THREE.Vector3; quaternion: THREE.Quaternion }
  updateFromProxy: (key: string, position: THREE.Vector3, quaternion: THREE.Quaternion) => void
}

export type SelectionGizmo = {
  attach: (object: THREE.Object3D, mode: GizmoMode) => void
  detach: () => void
  setOnChange: (cb: (() => void) | null) => void
}

export type SelectionHighlights = {
  applySelectionHighlight: (meshes: THREE.Mesh[]) => void
  clearSelectionHighlight: () => void
  clearHover: () => void
}

export type PartAllocationRequest = {
  hint: SelectableHint
  instanceIndex: number | null
  allInstances: boolean
}

export type SelectionHintInstances = {
  instanceOf: (hint: SelectableHint, mesh: THREE.Mesh) => number | null
  instanceMeshes: (hint: SelectableHint, index: number) => THREE.Mesh[]
}

export type ViewerSelectionDeps = {
  transforms: SelectionTransforms
  gizmo: SelectionGizmo
  highlights: SelectionHighlights
  hintInstances: SelectionHintInstances
  visibleMeshes: () => THREE.Mesh[]
  hintForMesh: (mesh: THREE.Mesh) => SelectableHint | null
  editable: () => boolean
  selectable: () => boolean
  allocationFor: (ebomPartId: string) => EbomPartInstanceAllocation | null
  requestRender: () => void
  reportUnusablePart: (hint: SelectableHint) => void
}

export function useViewerSelection(deps: ViewerSelectionDeps) {
  const selectedInstance = ref<string | null>(null)
  const selectedHint = shallowRef<SelectableHint | null>(null)
  const selectedInstanceIndex = ref<number | null>(null)
  const activeGizmo = ref<GizmoMode | null>(null)

  const proxy = new THREE.Object3D()
  let selectionMesh: THREE.Mesh | null = null
  let preserveOnSync = false

  const hasSelection = computed(() => !!selectedInstance.value || !!selectedHint.value)

  const canTransform = computed(
    () =>
      deps.editable() &&
      !!selectedInstance.value &&
      deps.transforms.isEditableInstance(selectedInstance.value),
  )

  const canAllocate = computed(() => deps.selectable() && !!selectedHint.value?.promoted)

  const selectedInstanceTotal = computed(() => instanceCountOf(selectedHint.value?.milliQuantity))

  const selectionAllocation = computed(() => {
    const id = selectedHint.value?.ebomPartId
    return id ? deps.allocationFor(id) : null
  })

  const selectionNeedsInstanceSelection = computed(
    () =>
      selectedInstanceIndex.value !== null &&
      needsInstanceSelection(selectionAllocation.value, selectedInstanceTotal.value),
  )

  const selectionAllocated = computed(() => {
    const index = selectedInstanceIndex.value
    if (index === null) return !!selectionAllocation.value
    return isInstanceAllocated(selectionAllocation.value, selectedInstanceTotal.value, index)
  })

  const canAllocateAllInstances = computed(
    () =>
      canAllocate.value &&
      selectedInstanceIndex.value !== null &&
      !allocatesAllInstances(selectionAllocation.value, selectedInstanceTotal.value),
  )

  const selectionKey = computed(() => {
    const hint = selectedHint.value
    if (hint) return `${hint.ebomPartId}:${selectedInstanceIndex.value ?? ''}`
    return selectedInstance.value
  })

  const selectionTitle = computed(() => {
    if (selectedHint.value) return selectedHint.value.articleNumber
    const key = selectedInstance.value
    return key ? key.slice(0, key.lastIndexOf(' ')) : ''
  })

  function selectionMeshes(): THREE.Mesh[] {
    if (selectedInstance.value) {
      return deps.transforms.meshesForInstance(selectedInstance.value).filter((m) => m.visible)
    }
    if (selectedHint.value) {
      return hintGroup(selectedHint.value, selectedInstanceIndex.value)
    }
    return []
  }

  function allocatableInstanceOf(hint: SelectableHint, mesh: THREE.Mesh): number | null {
    const total = instanceCountOf(hint.milliQuantity)
    const index = deps.hintInstances.instanceOf(hint, mesh)
    return total > 1 && index !== null && index < total ? index : null
  }

  function hintGroup(hint: SelectableHint, instance: number | null): THREE.Mesh[] {
    if (instance !== null) {
      return deps.hintInstances.instanceMeshes(hint, instance).filter((m) => m.visible)
    }
    return deps.visibleMeshes().filter((m) => deps.hintForMesh(m) === hint)
  }

  function clearSelection() {
    selectedInstance.value = null
    selectedHint.value = null
    selectedInstanceIndex.value = null
    activeGizmo.value = null
    selectionMesh = null
    preserveOnSync = false
    deps.highlights.clearSelectionHighlight()
    deps.gizmo.detach()
    deps.requestRender()
  }

  function applySelection(
    mesh: THREE.Mesh,
    allowFeedback: boolean,
    gizmoMode: GizmoMode | 'default' | null,
  ) {
    const instance = deps.transforms.instanceForMesh(mesh)
    const hint = deps.hintForMesh(mesh)
    const editableInstance =
      deps.editable() && !!instance && deps.transforms.isEditableInstance(instance)
    const allocatable = deps.selectable() && !!hint?.promoted

    if (!editableInstance && !allocatable) {
      clearSelection()
      if (allowFeedback && deps.selectable() && hint && !hint.promoted)
        deps.reportUnusablePart(hint)
      return
    }

    deps.highlights.clearHover()
    selectionMesh = mesh
    selectedInstance.value = editableInstance ? instance : null
    selectedHint.value = allocatable ? hint : null
    selectedInstanceIndex.value = allocatable && hint ? allocatableInstanceOf(hint, mesh) : null
    deps.highlights.applySelectionHighlight(selectionMeshes())

    const mode = gizmoMode === 'default' ? 'translate' : gizmoMode
    if (editableInstance && mode) showGizmo(mode)
    else {
      activeGizmo.value = null
      deps.gizmo.detach()
    }
    deps.requestRender()
  }

  function selectMesh(mesh: THREE.Mesh | null) {
    if (!mesh) {
      clearSelection()
      return
    }
    applySelection(mesh, true, 'default')
  }

  function showGizmo(mode: GizmoMode) {
    const instance = selectedInstance.value
    if (!instance || !canTransform.value) return
    const { position, quaternion } = deps.transforms.proxyStateFor(instance)
    proxy.position.copy(position)
    proxy.quaternion.copy(quaternion)
    proxy.updateMatrixWorld()
    activeGizmo.value = mode
    deps.gizmo.attach(proxy, mode)
  }

  function onGizmoToggle(mode: GizmoMode | null) {
    if (mode) showGizmo(mode)
  }

  function isHoverable(mesh: THREE.Mesh): boolean {
    if (deps.selectable() && deps.hintForMesh(mesh)?.promoted) return true
    if (deps.editable()) {
      const instance = deps.transforms.instanceForMesh(mesh)
      return !!instance && deps.transforms.isEditableInstance(instance)
    }
    return false
  }

  function hoverGroup(mesh: THREE.Mesh): THREE.Mesh[] {
    const hint = deps.hintForMesh(mesh)
    if (hint) return hintGroup(hint, allocatableInstanceOf(hint, mesh))
    const instance = deps.transforms.instanceForMesh(mesh)
    return instance ? deps.transforms.meshesForInstance(instance).filter((m) => m.visible) : [mesh]
  }

  function handleKey(e: KeyboardEvent): boolean {
    if (!hasSelection.value) return false
    if (e.key === 'Escape') {
      clearSelection()
      return true
    }
    if (!canTransform.value) return false
    if (e.key === 'g' || e.key === 'G') {
      showGizmo('translate')
      return true
    }
    if (e.key === 'r' || e.key === 'R') {
      showGizmo('rotate')
      return true
    }
    return false
  }

  function preserveThroughNextSync(preserve: boolean) {
    preserveOnSync = preserve
  }

  function acrossSync(resync: () => void) {
    const preserve = preserveOnSync
    const mesh = selectionMesh
    const mode = activeGizmo.value
    clearSelection()
    resync()
    if (preserve && mesh) applySelection(mesh, false, mode)
  }

  function attachTo(scene: THREE.Scene) {
    scene.add(proxy)
    deps.gizmo.setOnChange(() => {
      const instance = selectedInstance.value
      if (!instance) return
      deps.transforms.updateFromProxy(instance, proxy.position, proxy.quaternion)
    })
  }

  function dispose() {
    deps.gizmo.setOnChange(null)
    proxy.removeFromParent()
  }

  return {
    selectedHint,
    selectedInstanceIndex,
    selectedInstanceTotal,
    activeGizmo,
    hasSelection,
    canTransform,
    canAllocate,
    canAllocateAllInstances,
    selectionNeedsInstanceSelection,
    selectionAllocated,
    selectionKey,
    selectionTitle,
    selectionMeshes,
    clearSelection,
    selectMesh,
    onGizmoToggle,
    isHoverable,
    hoverGroup,
    handleKey,
    preserveThroughNextSync,
    acrossSync,
    attachTo,
    dispose,
  }
}
